<template>
  <div class="page">
    <breadcrumb />
    <div class="sub-cat-banner">
      <div class="sub-nav-title">
        {{ translationSubNav | translate | capitalize }}
      </div>
      <div class="sub-nav-emoji">
        {{ getSubNavItem | emoji }}
      </div>
      <div class="guide-me">
        <p>
          {{ "overview.titles.map" | translate }}
        </p>
        <icon-button class="icon" icon="geoAlt" @click="navigateToMap" />
      </div>
    </div>

    <div class="main">
      <div class="left-sidebar">
        <p class="left-nav-title">
          {{ `overview.titles.fashion_${chosenSex}` | translate }}
        </p>
        <p
          class="sub-nav-item"
          :class="{ 'sub-nav-item--selected': isSelectedSubNav(subNav) }"
          v-for="(subNav, i) in subNavs"
          :key="i"
          @click="onSubNavClick(subNav)"
        >
          {{ `filter.type.${subNav}` | translate | capitalize }}
        </p>
      </div>

      <div class="main-content">
        <div class="banner side-margins hidden">
          <h3 class="banner-title">
            {{ "overview.titles.banner" | translate }}
          </h3>
          <img
            class="logo"
            src="../assets/images/gyra_logo/gyra_logo@3x.png"
            alt="logo"
          />
          <div class="bottom">
            <p class="bottom-message">
              {{ "overview.titles.banner_bottom" | translate }}
            </p>
          </div>
        </div>

        <div class="filter-container--mobile">
          <h3 class="filter-title--mobile">
            {{ "filter.title.filter" | translate }}
          </h3>
          <div class="icon-container">
            <BIconFilter class="filter-toggle" @click="toggleFilter()" />
          </div>
          <slideup-mobile :is-open="isFilterOpen">
            <filter-mobile @close="toggleFilter()" @apply="applyClicked()" />
          </slideup-mobile>
        </div>

        <div class="filter-container side-margins">
          <h3>{{ "filter.title.filter" | translate }}</h3>
          <div class="filter-row">
            <div v-for="(filter, i) in selects" :key="i" class="filter-item">
              <gyra-filter :filter="filter" @input="onFilterChanged()" />
            </div>
          </div>
        </div>

        <div class="search-bar-container side-margins">
          <h3>{{ "filter.title.search_title" | translate }}</h3>
          <search-bar
            class="sb"
            :placeholder="'header.search' | translate"
            v-model="searchString"
            @input="onSearchStringInput"
          />
        </div>

        <div class="toggles side-margins">
          <h3>{{ "filter.title.focus" | translate }}</h3>
          <div class="toggle-cont">
            <toggle
              class="toggle"
              emoji="sustainable"
              label="product_map.sustainable"
              v-model="sustainable"
              @input="onFilterChanged"
            ></toggle>
            <toggle
              class="toggle"
              emoji="onSale"
              label="product_map.onSale"
              v-model="onSale"
              @input="onFilterChanged"
            ></toggle>
          </div>
        </div>

        <gyra-spinner v-if="isLoading" />

        <div v-else class="content side-margins">
          <div v-if="hasProducts" class="content-grid">
            <product-card
              v-for="(product, j) in products"
              :product="product"
              :key="j"
              @card-clicked="visitProductPage"
              class="product-card"
            />
          </div>
          <div v-else class="nothing-found">
            {{ "filter.messages.no_results" | translate }}
          </div>
        </div>
        <bottom-loader
          @intersection="loadMoreItems()"
          :stop-condition="this.isLoading || !this.hasProducts"
        ></bottom-loader>
      </div>
    </div>
  </div>
</template>

<script>
import GyraFilter from "../components/organisms/gyra-filter";
import ProductCard from "../components/molecules/product-card";
import BottomLoader from "../components/atoms/bottom-loader";
import { mapGetters, mapMutations } from "vuex";
import { BIconFilter } from "bootstrap-vue";
import GyraSpinner from "../components/atoms/gyra-spinner";
import { errorHandling } from "../mixins";
import { filterMixin } from "../mixins/filters/filterMixin";
import SlideupMobile from "../components/molecules/slideup-mobile";
import FilterMobile from "../components/organisms/filter-mobile";
import Breadcrumb from "../components/molecules/breadcrumb";
import { productTypeNavigation } from "../mixins/navigation/productTypeNavigation";
import { getPossibleFilterValues } from "../services/api";
import { filterModelMixin } from "../mixins/filters/filterModelMixin";
import Toggle from "../components/atoms/toggle";
import IconButton from "../components/atoms/icon-button";
import SearchBar from "../components/atoms/search-bar";

export default {
  name: "CategoryOverview",
  components: {
    SearchBar,
    IconButton,
    Toggle,
    Breadcrumb,
    FilterMobile,
    SlideupMobile,
    GyraSpinner,
    BottomLoader,
    ProductCard,
    GyraFilter,
    BIconFilter
  },
  mixins: [errorHandling, filterMixin, productTypeNavigation, filterModelMixin],
  props: {
    categoryProp: String,
    onSaleProp: Boolean,
    sustainableProp: Boolean
  },
  data() {
    return {
      isLoading: false,
      isFilterOpen: false,
      subNavs: []
    };
  },
  methods: {
    initFilters() {
      if (this.$route.params.sex) {
        this.setFilterFieldByKeyValue({
          key: "sex",
          value: this.$route.params.sex
        });
      }
      if (this.categoryProp) {
        this.setFilterFieldByKeyValue({
          key: "category",
          value: this.categoryProp
        });
      }
      if (this.sustainableProp) {
        this.setFilterFieldByKeyValue({
          key: "sustainable",
          value: this.sustainableProp
        });
      }
      if (this.onSaleProp) {
        this.setFilterFieldByKeyValue({
          key: "onSale",
          value: this.onSaleProp
        });
      }
    },
    async loadFilterResults() {
      this.isLoading = true;
      const products = await this.applyFilters();
      if (products) {
        this.setProducts(products);
      }
      this.isLoading = false;
    },
    async onFilterChanged() {
      await this.loadFilterResults();
      await this.setSelectOptions();
    },
    async loadMoreItems() {
      const productsToAdd = await this.applyFilters();
      if (productsToAdd) {
        this.addProducts(productsToAdd);
      }
    },
    visitProductPage(product) {
      this.$router.push({
        name: "ProductPage",
        params: {
          name: product.name,
          id: product._id,
          sex: this.$route.params.sex
        }
      });
    },
    toggleFilter() {
      this.isFilterOpen = !this.isFilterOpen;
    },
    applyClicked() {
      this.toggleFilter();
      this.loadFilterResults();
    },
    async getSubNavs() {
      let query = {
        sex: this.chosenSex
      };
      if (this.category) {
        query = { ...query, category: this.category };
      }

      const response = await getPossibleFilterValues("type", query);
      if (response.status === 200) {
        this.subNavs = response.data;
      } else {
        this.handleHttpError(response);
      }
    },
    onSubNavClick(subNav) {
      this.resetCommons();
      this.setSubNavItemWithFilter(subNav);
      this.onFilterChanged();
    },
    isSelectedSubNav(subNav) {
      return this.getSubNavItem === subNav;
    },
    navigateToMap() {
      this.$router.push({ name: "ProductMap" });
    },
    onSearchStringInput() {
      setTimeout(() => {
        this.loadFilterResults();
      }, 500);
    },
    ...mapMutations({
      addFilterOptions: "filters/addSelectOptions",
      toggleIsFullscreen: "globals/toggleIsFullscreen",
      resetFilters: "filters/resetSelections",
      resetCommons: "filters/resetCommons",
      setFilterFieldByKeyValue: "filters/setFieldByKeyValue",
      setProducts: "overview/setProducts",
      addProducts: "overview/addProducts"
    })
  },
  computed: {
    hasProducts() {
      return this.products && this.products.length > 0;
    },
    translationSubNav() {
      return this.getSubNavItem
        ? `filter.type.${this.getSubNavItem}`
        : "filter.title.overview";
    },
    chosenSex() {
      return this.$route.params.sex;
    },
    ...mapGetters({
      products: "overview/getProducts",
      selects: "filters/getSelects"
    })
  },
  async beforeMount() {
    this.initFilters();
    // await this.loadFilterResults();
    if (!this.products || this.products.length === 0) {
      await this.loadFilterResults();
    }
    await this.setSelectOptions();
  },
  async mounted() {
    await this.getSubNavs();
  },
  watch: {
    $route(to) {
      if (to.name !== "ProductPage") {
        this.resetFilters();
        this.setSelectOptions();
      }
      this.loadFilterResults();
      this.getSubNavs();
    },
    products() {
      if (!this.products) {
        this.loadFilterResults();
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "~@/styles/global.scss";
.page {
  position: relative;
  width: 100vw;
  overflow-x: hidden;
}
.hidden {
  display: none !important;
}

.filter-container {
  height: 100px;

  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  margin-top: 30px;

  display: none;
  @media (min-width: 601px) {
    display: flex;
  }
}
.filter-container--mobile {
  width: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;
  @media (min-width: 601px) {
    display: none;
  }

  .filter-title--mobile {
    font-size: 20px !important;
    margin: 19px 0 0 0;
    color: $gyra-grey-dark;
  }
  .icon-container {
    margin-top: 17px;
    width: 34px;
    height: 34px;
    border-radius: 12px;
    background-color: $gyra-grey-dark;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
.filter-toggle {
  cursor: pointer;
  font-size: 34px;
  z-index: 30;
  color: white;
}

h3 {
  font-size: 24px;
  font-weight: bold;
  @media (min-width: 800px) {
    font-size: 32px;
  }
}
.filter-row {
  width: 100%;
  display: flex;
  flex-direction: row;
  padding: 0;
  flex-wrap: wrap;
  @media (min-width: 905px) {
    flex-wrap: nowrap;
    justify-content: space-between;
  }
  @media (min-width: 1400px) {
    width: 80%;
  }
}
.filter-item {
  max-width: 250px;
  width: 20%;
  margin: 0 10px 10px;
  @media (min-width: 905px) {
    margin: 0 10px;
  }
}
.content {
  margin-top: 40px;
  margin-bottom: 40px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.content-grid {
  display: grid;
  grid-gap: 2rem;
  justify-items: center;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  @media (min-width: 1000px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  @media (min-width: 1328px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
  @media (min-width: 1828px) {
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  }
}

.nothing-found {
  display: flex;
  justify-content: center;
  margin-top: 60px;
  font-weight: 400;
  font-size: 24px;
  text-align: center;
}

.apply-button {
  display: flex;
  align-self: center;
  width: 50%;
  border: 1px solid $gyra-salmon;
  border-radius: 10px;
  margin-top: 10px;
  height: 40px;
  align-items: center;
  justify-content: center;
  font-size: 1em;
  color: $gyra-salmon;
  cursor: pointer;
}
.sub-cat-banner {
  background-color: $gyra-grey-lightest;
  height: 100px;
  @media (min-width: $gyra-breakpoint-lg) {
    height: 150px;
  }
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  .sub-nav-title {
    margin: 25px 20px 0 $gyra-margin-sides-mobile;
    @media (min-width: $gyra-breakpoint-lg) {
      margin: 25px 20px 0 $gyra-margin-sides;
      font-size: 50px;
    }
    font-size: 30px;
    font-weight: bold;
    font-family: Lora, serif;
  }
  .sub-nav-emoji {
    @media (min-width: $gyra-breakpoint-lg) {
      font-size: 50px;
    }
    font-size: 30px;
    margin-top: 25px;
    width: 75px;
    height: 75px;
  }

  .guide-me {
    margin-left: auto;
    margin-top: 25px;
    height: 45px;
    @media (min-width: $gyra-breakpoint-lg) {
      height: 75px;
    }
    align-items: center;
    margin-right: $gyra-margin-sides-mobile;
    @media (min-width: $gyra-breakpoint-md) {
      margin-right: $gyra-margin-sides;
    }
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 50px;
    @media (min-width: $gyra-breakpoint-sm) {
      width: 180px;
    }
    p {
      display: none;
      font-size: 14px;
      @media (min-width: $gyra-breakpoint-sm) {
        font-size: 16px;
        display: inline;
      }
      margin-bottom: 0;
      font-weight: bold;
    }

    .icon {
      background-color: $gyra-mint;
    }
  }
}
.main {
  display: flex;
  flex-direction: row;
}
.main-content {
  height: auto;
  width: 100%;
  padding-top: 0;
  @media (min-width: $gyra-breakpoint-lg) {
    padding-top: 70px;
  }
}

.left-sidebar {
  display: none;
  @media (min-width: $gyra-breakpoint-lg) {
    display: flex;
  }
  height: auto;
  width: 466px;
  padding-top: 70px;
  flex-direction: column;

  .left-nav-title {
    margin: 0 0 0 $gyra-margin-sides;
    font-size: 20px;
    font-weight: bold;
  }
  .sub-nav-item {
    margin: 0 0 0 150px;
    cursor: pointer;
  }
  .sub-nav-item--selected {
    font-weight: 700;
    text-decoration: underline;
  }
}

.side-margins {
  margin-right: 20px;
  margin-left: 20px;
  @media (min-width: $gyra-breakpoint-lg) {
    margin-right: $gyra-margin-sides;
    margin-left: 0;
  }
}

.toggles {
  display: none;
  @media (min-width: 601px) {
    display: flex;
  }
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: auto;
  padding-bottom: 20px;
  border-bottom: 3px solid $gyra-grey-dark;

  h3 {
    margin-top: 50px;
    width: auto;
  }

  .toggle-cont {
    margin-top: 28px;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    width: 40%;
    min-width: 450px;
  }

  .toggle {
    transform: scale(1.2);
  }
}

.search-bar-container {
  display: none;
  @media (min-width: 601px) {
    display: flex;
  }
  margin-top: 50px;
  width: auto;
  flex-direction: column;
  align-items: center;

  .sb {
    margin-top: 25px;
  }
}

.banner {
  position: relative;
  height: 145px;
  @media (min-width: $gyra-breakpoint-sm) {
    height: 343px;
  }

  width: auto;
  background-color: $gyra-salmon;

  display: flex;

  flex-direction: column;
  align-items: center;

  .banner-title {
    margin-top: 26px;
    margin-bottom: 0;
    font-size: 15px;
    font-weight: bold;
    @media (min-width: $gyra-breakpoint-sm) {
      font-size: 30px;
      margin-top: 57px;
    }
    @media (min-width: $gyra-breakpoint-xl) {
      font-size: 50px;
    }
  }

  .logo {
    margin-top: 13px;
    width: 25px;
    height: 32px;
    @media (min-width: $gyra-breakpoint-sm) {
      margin-top: 16px;
      width: 54px;
      height: 69px;
    }
  }

  .bottom {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 36px;
    @media (min-width: $gyra-breakpoint-sm) {
      height: 79px;
    }

    width: 100%;
    background-color: $gyra-grey-lightest;

    display: flex;
    justify-content: center;
    align-items: center;
  }

  .bottom-message {
    margin-bottom: 0;
    font-weight: bold;
    font-size: 9px;
    @media (min-width: $gyra-breakpoint-sm) {
      font-size: 18px;
    }
  }
}
</style>

<style lang="scss">
@import "~@/styles/global.scss";
.filter .vs__dropdown-toggle {
  border-radius: 5px;
  border-color: $gyra-grey-dark;
}
.filter .vs__selected-options {
  width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.filter .vs__dropdown-menu {
  min-width: fit-content;
}
</style>
